import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { Search, CheckSquare, Square, Wallet } from "lucide-react";
import { api, formatApiError, brl } from "@/lib/api";

export default function Receber() {
  const [sales, setSales] = useState([]);
  const [search, setSearch] = useState("");
  const [selected, setSelected] = useState([]);
  const [busy, setBusy] = useState(false);

  const fetchSales = () => {
    api.get("/sales", { params: { status: "fiado", paid: "false" } }).then((r) => setSales(r.data)).catch(() => {});
  };
  useEffect(() => { fetchSales(); }, []);

  const filtered = sales.filter((s) => s.client_name.toLowerCase().includes(search.toLowerCase()));
  const totalPendente = useMemo(() => sales.reduce((s, x) => s + x.total, 0), [sales]);
  const totalSelecionado = useMemo(
    () => sales.filter((s) => selected.includes(s.id)).reduce((s, x) => s + x.total, 0),
    [sales, selected]
  );

  const toggle = (id) => setSelected((prev) => (prev.includes(id) ? prev.filter((i) => i !== id) : [...prev, id]));

  const receive = async () => {
    if (!selected.length) {
      toast.error("Selecione pelo menos uma dívida");
      return;
    }
    setBusy(true);
    try {
      await api.post("/sales/receive", { ids: selected });
      toast.success(`Recebido: ${brl(totalSelecionado)}`);
      setSelected([]);
      fetchSales();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div data-testid="receber-page">
      <h1 className="font-display text-3xl sm:text-4xl font-light mb-6">Contas a Receber</h1>

      <div className="grid sm:grid-cols-3 gap-4 mb-6">
        <div className="bg-white border border-stone-200 rounded-2xl p-6 shadow-[0_2px_12px_rgba(0,0,0,0.03)]" data-testid="receber-total-pendente">
          <p className="text-xs font-semibold tracking-[0.15em] uppercase text-stone-500 mb-2">Total pendente em fiado</p>
          <p className="font-display text-3xl text-red-600">{brl(totalPendente)}</p>
        </div>
        <div className="bg-white border border-stone-200 rounded-2xl p-6 shadow-[0_2px_12px_rgba(0,0,0,0.03)]">
          <p className="text-xs font-semibold tracking-[0.15em] uppercase text-stone-500 mb-2">Dívidas em aberto</p>
          <p className="font-display text-3xl">{sales.length}</p>
        </div>
        <div className="bg-white border border-[#C8A279] rounded-2xl p-6 shadow-[0_2px_12px_rgba(0,0,0,0.03)] bg-[#F3EFE6]/50">
          <p className="text-xs font-semibold tracking-[0.15em] uppercase text-stone-500 mb-2">Selecionado</p>
          <p className="font-display text-3xl text-green-700" data-testid="receber-total-selecionado">{brl(totalSelecionado)}</p>
        </div>
      </div>

      <div className="bg-white border border-stone-200 rounded-2xl p-5 shadow-[0_2px_12px_rgba(0,0,0,0.03)]">
        <div className="relative mb-4 max-w-md">
          <Search size={16} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-stone-400" />
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Buscar por nome do cliente…"
            className="w-full rounded-full border border-stone-300 pl-10 pr-4 py-2.5 text-sm focus:outline-none focus:border-[#C8A279] transition-all"
            data-testid="receber-search-input"
          />
        </div>

        {filtered.length === 0 ? (
          <div className="py-14 text-center" data-testid="receber-empty-state">
            <Wallet size={32} className="mx-auto text-stone-300 mb-3" />
            <p className="text-sm text-stone-400">Nenhuma dívida pendente. Tudo em dia!</p>
          </div>
        ) : (
          <div className="space-y-2" data-testid="receber-list">
            {filtered.map((s) => (
              <div
                key={s.id}
                onClick={() => toggle(s.id)}
                className={`flex items-center gap-4 px-4 py-3.5 rounded-xl border cursor-pointer transition-colors ${
                  selected.includes(s.id) ? "bg-[#F0FDF4] border-green-300" : "border-stone-100 hover:border-stone-300"
                }`}
                data-testid={`receber-row-${s.id}`}
              >
                {selected.includes(s.id) ? (
                  <CheckSquare size={18} className="text-green-600 shrink-0" />
                ) : (
                  <Square size={18} className="text-stone-300 shrink-0" />
                )}
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium truncate">{s.client_name}</p>
                  <p className="text-xs text-stone-400 truncate">
                    {s.items.map((i) => i.name).join(", ")} · {new Date(s.sale_date + "T12:00:00").toLocaleDateString("pt-BR")}
                  </p>
                </div>
                <p className="text-sm font-semibold text-red-600" data-testid={`receber-amount-${s.id}`}>{brl(s.total)}</p>
              </div>
            ))}
          </div>
        )}

        <div className="flex flex-wrap gap-3 justify-end mt-6 pt-4 border-t border-stone-200">
          <button onClick={() => setSelected([])} className="px-6 py-3 rounded-full border border-stone-300 text-sm text-stone-600 hover:border-stone-900 transition-colors" data-testid="receber-cancel-button">
            Cancelar seleção
          </button>
          <button onClick={receive} disabled={busy} className="px-8 py-3 rounded-full bg-green-600 text-white text-sm font-medium hover:bg-green-700 transition-colors disabled:opacity-50" data-testid="receber-confirm-button">
            Receber {selected.length ? `(${brl(totalSelecionado)})` : ""}
          </button>
        </div>
      </div>
    </div>
  );
}
